/**
 * 中継エンドポイント（polaris-mono側、/api/spica-*）呼び出しラッパー。
 * NFR Design 1.3準拠: タイムアウト5〜10秒（AbortController）、非ブロッキング非同期処理。
 * U6タグはspica-apiを直接呼び出さず、この中継エンドポイントのみをfetchで呼び出す。
 * 契約詳細: domain-entities.md「中継エンドポイント契約」参照。
 */

import type { PageEventType, PageEventPayload } from "../types";

const RELAY_ENDPOINT = {
  RESOLVE_SCENARIO: "/api/spica-scenario/resolve",
  RECORD_SURVEY_RESPONSE: "/api/spica-survey/respond",
  AVAILABLE_SLOTS: "/api/spica-appointment/available-slots",
  CREATE_APPOINTMENT: "/api/spica-appointment/create",
  CREATE_APPOINTMENT_REQUEST: "/api/spica-appointment/request",
  CANCEL_APPOINTMENT: "/api/spica-appointment/cancel",
  GET_APPOINTMENT_DETAIL: "/api/spica-appointment/detail",
  GET_ICS: "/api/spica-appointment/ics",
  TRACK_PAGE_EVENT: "/api/spica-tracking/page-events",
  CANCELLABILITY: "/api/spica-appointment/cancellability",
  AVAILABILITY_RULE: "/api/spica-appointment/availability-rule",
} as const;

/** デフォルトタイムアウト（秒）。空き枠取得等で応答が遅い場合を考慮し30秒に設定。 */
const DEFAULT_TIMEOUT_SECONDS = 30;

/**
 * CSRFトークンのCookie名（polaris-mono側の`CsrfTokenSupplierFilter`が発行する`p_csrf_token`）。
 * このCookieは`HttpOnly=false`でJSから読み取れる。
 */
const CSRF_TOKEN_COOKIE_NAME = "p_csrf_token";

/**
 * CSRFトークンを送るフォームパラメータ名（polaris-mono側`CsrfTokenCheckFilter`が参照する`_csrf_token`）。
 * 既存のサービスサイトAPI呼び出しと同じ方式で、リクエストボディ（form-urlencoded）に載せて送る。
 */
const CSRF_TOKEN_PARAM_NAME = "_csrf_token";

/**
 * CookieからCSRFトークンを取得する。
 * 取得できない場合は空文字を返す（サーバ側で403となるが、タグ側からは制御不能なため送信は試みる）。
 */
function getCsrfToken(): string {
  const cookies = typeof document !== "undefined" ? document.cookie : "";
  const entry = cookies
    .split("; ")
    .find((c) => c.startsWith(`${CSRF_TOKEN_COOKIE_NAME}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_TOKEN_COOKIE_NAME.length + 1)) : "";
}

/**
 * 中継エンドポイントの実リクエストURLを組み立てる。
 *
 * 特設サイト（ホスト先頭が `pr.`）では、`/api/*` はサービスサイト（`pr.` を除いた通常ホスト）に対して
 * 呼び出す必要がある。特設サイトホスト（`pr.`）へ直接 `/api/*` を叩くと、オリジン（Apache）が
 * 特設サイト用に全パスへ `/pr` を前置してTomcatへプロキシするため `/pr/api/...` となり404になる。
 * これは既存サービスサイトJS（polaris-mono `request.js` の `createApiUrl`: `location.host.replace(/^pr\./,'')`）
 * と同一の方式で、特設サイト上のAPI呼び出しをサービスサイトホストへ向ける。
 *
 * ものづくり（`pr.` が付かない通常ホスト）ではホストがそのままとなり、従来どおり相対 `/api/*` 相当で動作する。
 * SSR等で `location` が利用できない場合は、フォールバックとして相対パス（引数のまま）を返す。
 */
function toApiUrl(path: string): string {
  if (typeof location === "undefined" || !location.host) {
    return path;
  }
  const host = location.host.replace(/^pr\./, "");
  return `${location.protocol}//${host}${path}`;
}

export interface RelayResponse<T> {
  ok: boolean;
  status: number;
  data: T | null;
  /** 中継エンドポイントが返す業務エラーコード（spica-apiのErrorCode enum値、例: SLOT_NOT_AVAILABLE） */
  businessErrorCode?: string;
  /**
   * 関連する既存アポイントID（業務エラー`DUPLICATE_COMPANY_APPOINTMENT`時のみ設定）。
   * 同一企業への重複アポイント時のキャンセル導線（「こちら」リンク）に用いる。
   */
  existingAppointmentId?: string;
  /** ネットワークエラー・タイムアウト等 */
  networkError: boolean;
  /**
   * サーバ側セッションで判定したログイン状態（レスポンスヘッダー`X-Spica-Logged-In`由来）。
   * ヘッダーが無い場合（ネットワークエラー等）はnull。
   */
  loggedIn: boolean | null;
}

interface RelayRequestOptions {
  timeoutSeconds?: number;
  /** trackPageEvent等のfire-and-forget送信で使用（応答を待たずに処理を継続） */
  fireAndForget?: boolean;
  /**
   * レスポンスボディの解釈方式（2026-09-09追加）。デフォルトは"json"（既存の全エンドポイントと同一動作）。
   * "text"はgetIcs専用: JSPが成功時に`text/calendar`で生のICSテキストを返す
   * （SpicaRelayJspSupport#buildClientOutput、ics=true時はJSON化しない）ため、response.json()を
   * 呼ばずresponse.text()で読む必要がある。従来は全エンドポイント共通でJSON解釈していたため、
   * ICSレスポンスが必ずJSONパース例外→catchでdata=nullとなり、ダウンロードが実行されない
   * 不具合があった。
   */
  responseType?: "json" | "text";
}

/**
 * 中継エンドポイントへPOSTリクエストを送信する。
 * NFR Design 1.1準拠: 自動リトライは行わない（呼び出し元が手動再試行導線を提供する）。
 */
async function post<T>(
  path: string,
  body: object,
  options: RelayRequestOptions = {}
): Promise<RelayResponse<T>> {
  const timeoutMs = (options.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS) * 1000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // 既存サービスサイトAPIと同じ方式（application/x-www-form-urlencoded）で送る。
    // JSONペイロードはrequestBodyフィールドに格納し、CSRFトークンは_csrf_tokenフィールドに載せる。
    // これにより中継先(spica-api)へはpolaris-mono側がJSONボディを再構築して転送する。
    const formBody = new URLSearchParams();
    formBody.set("requestBody", JSON.stringify(body));
    formBody.set(CSRF_TOKEN_PARAM_NAME, getCsrfToken());

    const response = await fetch(toApiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formBody,
      // 特設サイト→サービスサイトのクロスサブドメイン呼び出しでもCookie（セッション等）を送る。
      // 既存サービスサイトJS（analysis-log等の `withCredentials: true`）と同方針。
      credentials: "include",
      signal: controller.signal,
    });

    // サーバ側セッションで判定したログイン状態（ヘッダーが無ければnull）
    const loggedInHeader = response.headers?.get("X-Spica-Logged-In") ?? null;
    const loggedIn = loggedInHeader === null ? null : loggedInHeader === "true";

    let data: T | null = null;
    let businessErrorCode: string | undefined;
    let existingAppointmentId: string | undefined;
    try {
      if (options.responseType === "text") {
        // ICS等のtext/calendarレスポンス専用経路。失敗時（4xx/5xx）もボディはJSON化されないため
        // 生テキストのままdataへ格納する（呼び出し元でresponse.okを見て判定する）。
        const text = await response.text();
        data = (text.length > 0 ? text : null) as T | null;
      } else {
        const json = await response.json();
        if (response.ok) {
          data = json as T;
        } else {
          const errorJson = json as { businessError?: string; existingAppointmentId?: string };
          businessErrorCode = errorJson?.businessError;
          existingAppointmentId = errorJson?.existingAppointmentId;
          data = json as T;
        }
      }
    } catch {
      // レスポンスボディがJSON/テキストとして読めない場合（204等）は無視する
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      businessErrorCode,
      existingAppointmentId,
      networkError: false,
      loggedIn,
    };
  } catch {
    // タイムアウト（AbortError）・ネットワークエラー
    return {
      ok: false,
      status: 0,
      data: null,
      networkError: true,
      loggedIn: null,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

export const relayClient = {
  RELAY_ENDPOINT,

  resolveScenario<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.RESOLVE_SCENARIO, body);
  },

  recordSurveyResponse<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.RECORD_SURVEY_RESPONSE, body);
  },

  getAvailableSlots<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.AVAILABLE_SLOTS, body);
  },

  createAppointment<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.CREATE_APPOINTMENT, body);
  },

  createAppointmentRequest<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.CREATE_APPOINTMENT_REQUEST, body);
  },

  cancelAppointment<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.CANCEL_APPOINTMENT, body);
  },

  getAppointmentDetail<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.GET_APPOINTMENT_DETAIL, body);
  },

  getIcs<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.GET_ICS, body, { responseType: "text" });
  },

  /** BR-U6-6: fire-and-forget送信。失敗してもUXをブロックしない。 */
  trackPageEvent(body: { eventType: PageEventType; payload: PageEventPayload }): void {
    post(RELAY_ENDPOINT.TRACK_PAGE_EVENT, body, { fireAndForget: true }).catch(() => {
      // ベストエフォート。コンソール警告のみ（呼び出し元で出力）。
    });
  },

  /**
   * 2026-08-26新設: キャンセル可否の事前判定（BR-U2-5参照）。
   * AppointmentCancelViewのページ表示時に呼ぶ、状態変更を伴わない読み取り専用エンドポイント。
   */
  getCancellability<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.CANCELLABILITY, body);
  },

  /**
   * 対応可能ルール取得（営業時間・除外日・最小リードタイムのみ）。
   * 「都合の良い日時がない場合はこちら」導線がカレンダーの選択可否をアポ受付設定へ反映するために利用する。
   */
  getAvailabilityRule<T>(body: object): Promise<RelayResponse<T>> {
    return post<T>(RELAY_ENDPOINT.AVAILABILITY_RULE, body);
  },
};
